import { z } from 'zod';

/**
 * Canonical vocabulary of "preview" entity types.
 *
 * A preview type identifies a domain entity that the client can open in a
 * guide/preview panel and that the AI may reference via a
 * `#preview=<type>&id=<id>` markdown anchor. This enum is the single source of
 * truth for that contract: it is *produced* by the AI prompt (server) and
 * *consumed* by the client router, markdown renderer, and tool-result badges.
 *
 * Previously the valid-type list was triplicated (client `route-utils`,
 * `DataAttachmentTypeSchema`, `TripsState.showGuide`) and had already drifted —
 * if the prompt emitted a type one copy omitted, that platform rendered a dead
 * link while another worked. Keep additions here and let every consumer derive
 * from this enum so the vocabulary cannot drift again.
 *
 * NOTE: This module must stay platform-neutral (no DOM/Node-only APIs) so it
 * bundles cleanly for the web client and React Native / Metro alike.
 */
export const PreviewTypeSchema = z.enum(['destination', 'hotel', 'room', 'flight', 'place']);

export type PreviewType = z.infer<typeof PreviewTypeSchema>;

/** Ordered list of every valid preview type, for runtime validation and iteration. */
export const PREVIEW_TYPES = PreviewTypeSchema.options;

/** Type guard narrowing an arbitrary value to a valid {@link PreviewType}. */
export function isPreviewType(value: unknown): value is PreviewType {
  return PreviewTypeSchema.safeParse(value).success;
}

/** Parsed target of a preview link: a validated type plus an entity id. */
export interface PreviewLinkTarget {
  type: PreviewType;
  id: string;
}

const PREVIEW_HASH_PREFIX = '#preview=';

/**
 * Builds the `#preview=<type>&id=<id>` hash fragment for a preview target.
 *
 * @example
 * buildPreviewHref({ type: 'hotel', id: 'abc' }); // => '#preview=hotel&id=abc'
 */
export function buildPreviewHref({ type, id }: PreviewLinkTarget): string {
  return `${PREVIEW_HASH_PREFIX}${type}&id=${encodeURIComponent(id)}`;
}

/**
 * Builds an inline markdown preview link, the exact shape the AI prompt is
 * instructed to emit (e.g. `[Hotel Name](#preview=hotel&id=_id)`).
 *
 * @example
 * buildPreviewMarkdownLink('The Mark', 'hotel', 'abc'); // => '[The Mark](#preview=hotel&id=abc)'
 */
export function buildPreviewMarkdownLink(label: string, type: PreviewType, id: string): string {
  return `[${label}](${buildPreviewHref({ type, id })})`;
}

/**
 * Parses a `#preview=<type>&id=<id>` href into its {@link PreviewLinkTarget},
 * validating the type against the canonical vocabulary. Returns `undefined` for
 * non-preview hrefs, missing ids, or types outside {@link PreviewTypeSchema}.
 *
 * Uses manual key/value splitting (rather than `URLSearchParams`) to stay
 * portable across runtimes whose `URLSearchParams` implementation is partial.
 *
 * @example
 * parsePreviewHref('#preview=hotel&id=abc'); // => { type: 'hotel', id: 'abc' }
 * parsePreviewHref('#other=value');          // => undefined
 */
export function parsePreviewHref(href: string): PreviewLinkTarget | undefined {
  if (!href.startsWith(PREVIEW_HASH_PREFIX)) return;

  const params: Record<string, string> = {};
  for (const pair of href.slice(1).split('&')) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    const key = pair.slice(0, eq);
    const value = pair.slice(eq + 1);
    if (!key || !value) continue;
    try {
      params[decodeURIComponent(key)] = decodeURIComponent(value);
    } catch {
      params[key] = value;
    }
  }

  const type = params.preview;
  const id = params.id;
  if (!type || !id || !isPreviewType(type)) return;
  return { type, id };
}
